import { useEffect, useState } from 'react';
import { Box, Card, Typography, TextField } from '@mui/material';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../Buttons';
import { LoadingUI } from '../../LayoutStyles';
import API from '../../../api';
import { CustomAutocomplete } from '../../StyledInputs';
import { usePopup } from '../../../context/PopupContext';

export default function IssuesCard({ canEdit, issues = [], eventId, disabledButton }) {
  const { showPopup, setPopUpText } = usePopup();

  const [issuesList, setIssuesList] = useState([]);
  const [isIssuesListFetching, setIsIssuesListFetching] = useState(false);

  const initialSelected = (issues || []).map((item) => item.issue || item);
  const [selectedIssues, setSelectedIssues] = useState(initialSelected);

  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const getIssuesList = () => {
    setIsIssuesListFetching(true);
    API.get('event_issues')
      .then((response) => {
        setIssuesList(response.data || []);
      })
      .finally(() => setIsIssuesListFetching(false));
  };

  useEffect(() => {
    if (canEdit) getIssuesList();
  }, [canEdit]);

  useEffect(() => {
    setSelectedIssues((issues || []).map((item) => item.issue || item));
  }, [issues]);

  useEffect(() => {
    if (!issuesList || !issuesList.length) return;

    const incoming = (issues || [])
      .map((it) => {
        if (it.issue_id) return String(it.issue_id);
        if (it.issue && it.issue.id) return String(it.issue.id);
        if (it.id) return String(it.id);
        return null;
      })
      .filter(Boolean);

    if (incoming.length) {
      const mapped = incoming.map((id) => issuesList.find((opt) => String(opt.id) === id)).filter(Boolean);

      const notFound = (selectedIssues || []).filter((s) => !mapped.find((m) => String(m.id) === String(s.id)));

      const final = [...mapped, ...notFound];
      setSelectedIssues(final);
    }
  }, [issuesList]);

  useEffect(() => {
    const originalIds = (issues || [])
      .map((it) => {
        if (it.issue_id) return String(it.issue_id);
        if (it.issue && it.issue.id) return String(it.issue.id);
        if (it.id) return String(it.id);
        return null;
      })
      .filter(Boolean)
      .sort();

    const currentIds = (selectedIssues || [])
      .map((s) => String(s.id))
      .filter(Boolean)
      .sort();
    setHasChanges(JSON.stringify(originalIds) !== JSON.stringify(currentIds));
  }, [selectedIssues, issues]);

  const updateEventIssues = () => {
    const payload = (selectedIssues || []).map((s) => s.id);
    const data = { event_id: eventId, issues: payload };

    API.post(`/event_issues`, data)
      .then((response) => {
        setIsEditing(false);
        setHasChanges(false);
        setPopUpText(response.data?.message || 'Aggiornato');
        showPopup(true);
      })
      .catch((err) => console.error(err));
  };

  const mergedOptions = (() => {
    const byId = new Map();
    (issuesList || []).forEach((o) => byId.set(String(o.id), o));
    (selectedIssues || []).forEach((o) => {
      if (o && o.id && !byId.has(String(o.id))) byId.set(String(o.id), o);
    });
    return Array.from(byId.values());
  })();

  return (
    <Card
      sx={{
        padding: '24px',
        marginBottom: '24px',
        maxHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Materie Evento</Typography>
      </Box>

      {isIssuesListFetching ? (
        LoadingUI()
      ) : (
        <>
          <CustomAutocomplete
            multiple
            disableCloseOnSelect
            readOnly={!canEdit}
            options={mergedOptions}
            value={selectedIssues}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              return String(option.id) === String(value.id);
            }}
            getOptionLabel={(option) => {
              if (!option) return '';
              return (option.description || option.label || '').toString().trim();
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {option.description || option.label}
              </Box>
            )}
            onChange={(e, value) => setSelectedIssues(value)}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Materie evento" />}
          />

          {hasChanges && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <DefaultOutlinedButton
                onClick={() => {
                  setSelectedIssues((issues || []).map((it) => it.issue || it));
                  setIsEditing(false);
                }}
              >
                Annulla
              </DefaultOutlinedButton>
              <DefaultContainedButton onClick={updateEventIssues}>Salva</DefaultContainedButton>
            </Box>
          )}
        </>
      )}
    </Card>
  );
}
